import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./context/AuthContext";
import { FeatureAccessProvider } from "./context/FeatureAccessContext";
import { ThemeProvider } from "./context/ThemeContext";
import ProtectedRoute from "./components/ProtectedRoute";
import FeatureGate from "./components/FeatureGate";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import AppShell from "./pages/AppShell";
import Dashboard from "./pages/Dashboard";
import Properties from "./pages/Properties";
import RentComps from "./pages/RentComps";
import News from "./pages/News";
import LoanRebalancer from "./pages/LoanRebalancer";
import Compare from "./pages/Compare";
import Calculators, { CalcPopout } from "./pages/Calculators";
import InsightsPage from "./pages/InsightsPage";
import Advisor from "./pages/Advisor";
import Portfolio from "./pages/Portfolio";
import PropertyHistory from "./pages/PropertyHistory";
import Pricing from "./pages/Pricing";
import Profile from "./pages/Profile";
import Admin from "./pages/Admin";
import Guide from "./pages/Guide";
import Referrals from "./pages/Referrals";
import Tenants from "./pages/Tenants";
import Projects from "./pages/Projects";
import TenantDashboard from "./pages/TenantDashboard";
import Will from "./pages/Will";
import LawyerReview from "./pages/LawyerReview";
import WitnessSign from "./pages/WitnessSign";
import LawyerDashboard from "./pages/LawyerDashboard";
import LawyerRegister from "./pages/LawyerRegister";
import DisclaimerPage from "./pages/DisclaimerPage";
import SharedReport from "./pages/SharedReport";
import WalkthroughShare from "./pages/WalkthroughShare";
import WalkthroughEmbed from "./pages/WalkthroughEmbed";
import { LegalTerms, LegalPrivacy } from "./pages/Legal";
import AuthCallback from "./pages/AuthCallback";
import FirebaseDebug from "./pages/FirebaseDebug";
import SEOIndex from "./pages/SEOIndex";
import SEOPage from "./pages/SEOPage";
import AnalyticsTracker from "./components/AnalyticsTracker";
import ActivityTracker from "./components/ActivityTracker";

function Router() {
  const location = useLocation();
  // Emergent OAuth returns with #session_id=... — handle synchronously before any other routing
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <AnalyticsTracker />
      <ActivityTracker />
      <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/disclaimer" element={<DisclaimerPage />} />
      <Route path="/share/:id" element={<SharedReport />} />
      <Route path="/walkthrough/share/:token" element={<WalkthroughShare />} />
      <Route path="/embed/walkthrough/:slug" element={<WalkthroughEmbed daily={false} />} />
      <Route path="/embed/demo-of-the-day" element={<WalkthroughEmbed daily={true} />} />
      <Route path="/calc-popout/:tabId" element={<CalcPopout />} />
      <Route path="/insights/:slug" element={<InsightsPage />} />
      <Route path="/seo" element={<SEOIndex />} />
      <Route path="/seo/:slug" element={<SEOPage />} />
      <Route path="/legal/terms" element={<LegalTerms />} />
      <Route path="/legal/privacy" element={<LegalPrivacy />} />
      <Route path="/debug/firebase" element={<FirebaseDebug />} />
      <Route path="/lawyer-review/:token" element={<LawyerReview />} />
      <Route path="/witness-sign/:token" element={<WitnessSign />} />
      <Route path="/lawyer/register" element={<LawyerRegister />} />
      <Route
        path="/lawyer"
        element={
          <ProtectedRoute>
            <LawyerDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenant"
        element={
          <ProtectedRoute>
            <TenantDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="properties" element={<Properties />} />
        <Route path="rent-comps" element={<FeatureGate slug="rent-comps"><RentComps /></FeatureGate>} />
        <Route path="news" element={<FeatureGate slug="news"><News /></FeatureGate>} />
        <Route path="loans" element={<FeatureGate slug="loan-rebalancer"><LoanRebalancer /></FeatureGate>} />
        <Route path="portfolio" element={<Portfolio />} />
        <Route path="property-history" element={<FeatureGate slug="property-history"><PropertyHistory /></FeatureGate>} />
        <Route path="tenants" element={<FeatureGate slug="tenants"><Tenants /></FeatureGate>} />
        <Route path="projects" element={<FeatureGate slug="projects"><Projects /></FeatureGate>} />
        <Route path="will" element={<FeatureGate slug="will"><Will /></FeatureGate>} />
        <Route path="compare" element={<FeatureGate slug="compare"><Compare /></FeatureGate>} />
        <Route path="calculators" element={<FeatureGate slug="calculators"><Calculators /></FeatureGate>} />
        <Route path="advisor" element={<FeatureGate slug="advisor"><Advisor /></FeatureGate>} />
        <Route path="guide" element={<Guide />} />
        <Route path="referrals" element={<Referrals />} />
        <Route path="profile" element={<Profile />} />
        <Route path="admin" element={<Admin />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
          <FeatureAccessProvider>
            <BrowserRouter>
              <Toaster
                position="top-right"
                theme="system"
                toastOptions={{
                  style: {
                    background: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    color: "hsl(var(--foreground))",
                  },
                }}
              />
              <Router />
            </BrowserRouter>
          </FeatureAccessProvider>
        </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
